import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Container from '../shared/Container';
import Button from '../shared/Button';
import { Link } from 'react-router-dom';
import './AboutSection.css';

const AboutSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previousImageIndex, setPreviousImageIndex] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const autoPlayTimerRef = useRef(null);

  const images = [
    '/images/about/about1.jpg',
    '/images/about/about2.jpg',
    '/images/about/about3.jpg',
    '/images/about/about4.jpg',
    '/images/about/about5.jpg',
    '/images/about/about6.jpg',
    '/images/about/about7.jpg',
    '/images/about/about8.jpg',
  ];

  // Auto-advance the slideshow
  useEffect(() => {
    if (isAutoPlaying && !isAnimating) {
      autoPlayTimerRef.current = setInterval(() => {
        advanceSlide();
      }, 6000); // Longer interval for slower animations
    }

    return () => {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current);
      }
    };
  }, [isAutoPlaying, isAnimating]);

  // Advance to the next slide
  const advanceSlide = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setPreviousImageIndex(currentImageIndex);
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    
    // Reset animation lock after animation completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 1050); // Slightly longer than animation duration
  };

  // Pause auto-play on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  // Handle thumbnail click
  const handleThumbnailClick = (index) => {
    if (isAnimating || index === currentImageIndex) return;
    
    setIsAnimating(true);
    setPreviousImageIndex(currentImageIndex);
    setCurrentImageIndex(index);
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 1050);
  };

  // Get images for thumbnails (next 3 images after current)
  const getThumbIndex = (offset) => (currentImageIndex + offset) % images.length;

  return (
    <section className="py-16 bg-brand-cream">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* Main Image - Left Side */}
          <div className="main-image-container">
            {/* Mobile Image - Only visible on mobile */}
            <div className="sm:hidden">
              <AnimatePresence initial={false}>
                {/* Current image sliding in */}
                <motion.img 
                  key={`current-${currentImageIndex}`}
                  src={images[currentImageIndex]} 
                  alt="The Original Pancake House" 
                  className="main-image"
                  loading="lazy"
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  transition={{ duration: 1, ease: "easeInOut" }}
                />
                
                {/* Previous image sliding out */}
                {previousImageIndex !== null && (
                  <motion.img 
                    key={`prev-${previousImageIndex}`}
                    src={images[previousImageIndex]} 
                    alt="The Original Pancake House" 
                    className="main-image previous"
                    loading="lazy"
                    initial={{ x: 0 }}
                    animate={{ x: "-100%" }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  />
                )}
              </AnimatePresence>
            </div>
            
            {/* Desktop Image - Only visible on desktop */}
            <div className="hidden sm:block">
              <AnimatePresence initial={false}>
                {/* Current image sliding in */}
                <motion.img 
                  key={`current-desktop-${currentImageIndex}`}
                  src={images[currentImageIndex]} 
                  alt="The Original Pancake House" 
                  className="main-image"
                  loading="lazy"
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  transition={{ duration: 1, ease: "easeInOut" }}
                />
                
                {/* Previous image sliding out */}
                {previousImageIndex !== null && (
                  <motion.img 
                    key={`prev-desktop-${previousImageIndex}`}
                    src={images[previousImageIndex]} 
                    alt="The Original Pancake House" 
                    className="main-image previous"
                    loading="lazy"
                    initial={{ x: 0 }}
                    animate={{ x: "-100%" }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>
          
          {/* About Text and Thumbnails - Right Side */}
          <div className="about-content flex flex-col h-full justify-end">
            <motion.h2 
              className="text-3xl md:text-4xl font-heading text-brand-navy mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              ABOUT OPH
            </motion.h2>
            
            <motion.div
              className="text-base space-y-4 mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p>
                The Original Pancake House was founded in Portland, Oregon in 1953 by Les Highet and Erma Hueneke. Drawing 
                upon their many years of experience in the culinary field, and their extensive working knowledge of authentic 
                national and ethnic pancake recipes they were able to offer, without compromise, this unique and original menu 
                which has gained national acclaim.
              </p>
              <p>
                The Original Pancake House is a second and third generation family business, 
                which takes great pride in maintaining the high standards that make our food so outstanding. There are now over 
                one hundred franchises from coast to coast, which have won local and national acclaim. Our recipes demand only 
                the very finest of ingredients, such as 93 score butter, pure 36% whipping cream, fresh grade AA eggs, hard wheat 
                unbleached flour, and our own recipe sourdough starter. Our batters and sauces are made fresh in each 
                restaurant's kitchen.
              </p>
              
              <div className="flex space-x-4 mt-8">
                <Button
                  as={Link}
                  to="/about"
                  variant="solid"
                  color="navy"
                >
                  About Our Story
                </Button>
                
                <Button
                  as={Link}
                  to="/locations"
                  variant="outline"
                  color="navy"
                >
                  Find a location
                </Button>
              </div>
            </motion.div>
            
            {/* Thumbnails under the text - desktop only */}
            <div 
              className="hidden sm:flex thumbnails-container"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {[1, 2, 3].map((offset) => {
                const thumbIndex = getThumbIndex(offset);
                return (
                  <div 
                    key={`thumb-container-${offset}`}
                    className={`thumbnail ${thumbIndex === currentImageIndex ? 'active' : ''}`}
                    onClick={() => handleThumbnailClick(thumbIndex)}
                  >
                    <AnimatePresence initial={false}>
                      <motion.img 
                        key={`thumb-img-${thumbIndex}`}
                        src={images[thumbIndex]} 
                        alt={`The Original Pancake House ${offset}`} 
                        className="w-full h-full object-cover rounded-md shadow-md"
                        loading="lazy"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "-100%" }}
                        transition={{ 
                          duration: 1,
                          delay: 0.05,
                          ease: "easeInOut"
                        }}
                      />
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default AboutSection;